// useScreenTimeFirebase.js
import { useEffect, useMemo, useRef, useState } from "react";
import { ref, runTransaction, onValue, serverTimestamp } from "firebase/database";
import { environment } from "utils/constant";

// --- helpers
function dateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function uuidish() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `u-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/**
 * Tracks active screen time (focused + visible + not idle) and writes to RTDB.
 *
 * Options:
 *  - rtdb:      Firebase RTDB instance (getDatabase(app))
 *  - userId:    string | null  (pass null when logged out)
 *  - pathBase:  base path in RTDB (default "screenTime")
 *  - appId:     optional "web", "admin", etc (not stored right now, but reserved)
 *  - idleTimeoutMs: idle cutoff (default 60_000 ms)
 *  - flushIntervalMs: periodic write interval (default 60_000 ms)
 *  - storageKeyBase: localStorage prefix (default "screen_time_pending")
 */
export function useScreenTimeFirebase({
  rtdb,
  userId,
  pathBase = `screenTime-${environment}`,
  appId = "web",
  idleTimeoutMs = 60_000,
  flushIntervalMs = 60_000,
  storageKeyBase = "screen_time_pending",
}) {
  const [active, setActive] = useState(false);
  const [todayMs, setTodayMs] = useState(0);

  // track current day so we can resubscribe at midnight
  const [currentDayKey, setCurrentDayKey] = useState(dateKey());

  const pendingBucketsRef = useRef({});       // { "YYYY-MM-DD": ms }
  const trackingStartRef = useRef(null);      // performance.now() when segment started
  const lastInputRef = useRef(performance.now());
  const isIdleRef = useRef(true);

  const sessionIdRef = useRef(uuidish());
  const payloadCounterRef = useRef(0);

  const storageKey = useMemo(
    () => `${storageKeyBase}:${userId ?? "anon"}:${appId}`,
    [storageKeyBase, userId, appId]
  );

  // ---- local pending persistence
  const persistPending = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(pendingBucketsRef.current));
    } catch {}
  };
  const loadPending = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      pendingBucketsRef.current = raw ? JSON.parse(raw) : {};
    } catch {
      pendingBucketsRef.current = {};
    }
  };

  // ---- accumulate delta into the right day bucket
  const addDelta = (deltaMs) => {
    if (!Number.isFinite(deltaMs) || deltaMs <= 0) return;
    const key = dateKey();
    pendingBucketsRef.current[key] = (pendingBucketsRef.current[key] || 0) + deltaMs;
  };

  // ---- activate/deactivate segment
  const activate = () => {
    if (!document.hidden && document.hasFocus()) {
      isIdleRef.current = false;
      if (trackingStartRef.current == null) trackingStartRef.current = performance.now();
      setActive(true);
    }
  };
  const deactivate = () => {
    if (trackingStartRef.current != null) {
      const now = performance.now();
      addDelta(now - trackingStartRef.current);
      trackingStartRef.current = null;
      persistPending();
      // reflect local pending (today) quickly
      setTodayMs((pendingBucketsRef.current[dateKey()] || 0));
    }
    setActive(false);
  };

  // ---- user input -> leave idle
  const markActivity = () => {
    lastInputRef.current = performance.now();
    if (document.hidden || !document.hasFocus()) return;
    if (isIdleRef.current) {
      isIdleRef.current = false;
      activate();
    }
  };

  // ---- lifecycle: listeners + idle timer
  useEffect(() => {
    // visibility / focus
    const onVisibility = () => {
      if (!document.hidden && document.hasFocus() && !isIdleRef.current) activate();
      else deactivate();
    };
    const onFocus = () => (!document.hidden && !isIdleRef.current ? activate() : deactivate());
    const onBlur = () => deactivate();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    // inputs reset idle
    const inputs = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    inputs.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    // idle checker
    const idleId = setInterval(() => {
      if (trackingStartRef.current == null) return;
      const now = performance.now();
      if (now - lastInputRef.current > idleTimeoutMs) {
        isIdleRef.current = true;
        deactivate();
      }
    }, 1000);

    // start immediately if focused
    if (!document.hidden && document.hasFocus()) markActivity();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      inputs.forEach((e) => window.removeEventListener(e, markActivity));
      clearInterval(idleId);
    };
  }, [idleTimeoutMs]);

  // ---- keep todayMs ticking locally + roll day at midnight
  useEffect(() => {
    loadPending();
    const uiId = setInterval(() => {
      const dk = dateKey();
      setTodayMs((pendingBucketsRef.current[dk] || 0));
      if (dk !== currentDayKey) setCurrentDayKey(dk);
    }, 1000);
    return () => clearInterval(uiId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // ---- subscribe to server value for today (refreshes on login + at midnight)
  useEffect(() => {
    if (!userId) return;
    const nodeRef = ref(rtdb, `/${pathBase}/${userId}/${appId}/${currentDayKey}/activeMs`);
    const unsubscribe = onValue(nodeRef, (snap) => {
      const serverMs = snap.val() || 0;
      const localMs = pendingBucketsRef.current[currentDayKey] || 0;
      setTodayMs(serverMs + localMs);
    });
    return () => unsubscribe();
  }, [rtdb, userId, pathBase, currentDayKey]);

  // ---- flush pending to RTDB (idempotent per day via payload marker)
  const flush = async () => {
    // close open segment
    if (trackingStartRef.current != null && !isIdleRef.current && !document.hidden && document.hasFocus()) {
      const now = performance.now();
      addDelta(now - trackingStartRef.current);
      trackingStartRef.current = now; // continue new segment post-flush
    }
    persistPending();

    if (!userId) return; // not logged in -> keep locally

    const entries = Object.entries(pendingBucketsRef.current).filter(([, ms]) => ms > 0);
    if (entries.length === 0) return;

    for (const [day, ms] of entries) {
      const payloadId = `${sessionIdRef.current}-${++payloadCounterRef.current}-${day}`;
      const dayRef = ref(rtdb, `/${pathBase}/${userId}/${appId}/${day}`);

      try {
        await runTransaction(dayRef, (curr) => {
          const node = curr || {};
          const payloads = node.payloads || {};
          if (payloads[payloadId]) return node; // already applied

          const next = (node.activeMs || 0) + Math.floor(ms);
          return {
            activeMs: next,
            payloads: { ...payloads, [payloadId]: true },
            updatedAt: serverTimestamp(),
          };
        });

        // clear only if success
        delete pendingBucketsRef.current[day];
        persistPending();
      } catch (e) {
        // keep local; will retry next flush
        // console.error("flush error", e);
      }
    }

    // update UI quickly (server sub will also update)
    const dk = dateKey();
    setTodayMs((pendingBucketsRef.current[dk] || 0));
  };

  // ---- periodic flush + page lifecycle
  useEffect(() => {
    const id = setInterval(() => { flush(); }, flushIntervalMs);
    const onPageHide = () => { flush(); };
    const onBeforeUnload = () => { flush(); };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      clearInterval(id);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushIntervalMs, userId]);

  // ---- call this BEFORE auth.signOut()
  const prepareForLogout = async () => {
    await flush();   // last write while rules still allow it
    deactivate();    // stop counting
    pendingBucketsRef.current = {};
    persistPending();
    setTodayMs(0);
  };

  return {
    active,
    todayMs,          // server(for today) + local pending
    flush,            // manual sync button if you want
    prepareForLogout, // call before signOut()
    sessionId: sessionIdRef.current,
  };
}
